import { useRef, useState, type ChangeEvent } from 'react';
import {
  Button,
  Input,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  Camera24Regular,
  CloudArrowUpRegular,
  DeleteRegular,
  FolderOpenRegular,
} from '@fluentui/react-icons';
import { uploadImage } from '@moko/utils/src/uploadHelper';
import { getCapturedMediaFileName } from '@moko/utils/src/videoRecordingUtils';
import type { IUploadFileResponse } from '@moko/utils/src/interfaces/UploadFileInterfaces';
import RenderIf from './RenderIf';
import ImageTaker from './ImageTaker';
import { ImagePreviewDialog } from './ImagePreviewDialog';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXXS,
    minWidth: 0,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    minWidth: 0,
    overflow: 'hidden',
  },
  hiddenInput: {
    display: 'none',
  },
  fileName: {
    flex: 1,
    minWidth: 0,
  },
  previewImage: {
    width: '250px',
  },
  thumbnail: {
    width: '32px',
    height: '32px',
    objectFit: 'cover',
    borderRadius: tokens.borderRadiusSmall,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

export interface IImageSelectorProps {
  value: string;
  imageSource?: string;
  imageLocation?: { latitude: number; longitude: number } | null;
  uploadImage: boolean;
  purpose: string;
  onUpload?: (
    id: number,
    url: string,
    name: string,
    location?: { latitude: number; longitude: number } | undefined
  ) => void; // location type matches ImageTaker
  isTextFile?: boolean;
  onFileSelected?: (name: string, content: string | ArrayBuffer) => void;
  imageSelectionAllowed?: boolean; // if false, show camera button instead
  disabled?: boolean; // when true, hide the capture/select/upload/remove actions (view only)
  allowVideo?: boolean;
}

export const ImageSelector = (params: IImageSelectorProps) => {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [isSelected, setIsSelected] = useState(false);
  const [showImageTaker, setShowImageTaker] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const styles = useStyles();
  const fileUploadRef = useRef<HTMLInputElement | null>(null);

  const changeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setSelectedFile(file);
    setIsSelected(!!file);
  };

  const [uploading, setUploading] = useState(false);

  // Store location if captured from ImageTaker
  const [capturedLocation, setCapturedLocation] = useState<
    { latitude: number; longitude: number } | undefined
  >(undefined);

  const handleUploadClick = () => {
    if (!selectedFile?.name) {
      return;
    }
    const blob: Blob = selectedFile as Blob;
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      const result = reader.result;
      const content = params.isTextFile
        ? (result as string)
        : (result as ArrayBuffer);
      if (params.onFileSelected) {
        params.onFileSelected(selectedFile?.name, content);
      }
      if (params.uploadImage) {
        if (!navigator.onLine) {
          if (params.onUpload) {
            params.onUpload(
              0,
              result as string,
              selectedFile?.name,
              capturedLocation
            );
          }
          return;
        }
        const abortController = new AbortController();
        setUploading(true);
        uploadImage(
          abortController,
          selectedFile?.name,
          params.purpose,
          content as ArrayBuffer
        )
          .then((data: IUploadFileResponse) => {
            if (params.onUpload) {
              params.onUpload(
                data.id,
                data.url,
                selectedFile?.name,
                capturedLocation
              );
            }
          })
          .finally(() => setUploading(false));
      }
    });
    if (params.isTextFile) {
      reader.readAsText(blob);
    } else {
      reader.readAsDataURL(blob);
    }
  };

  // Remove extension for thumbnail, always use .png
  function removeExtension(filename?: string) {
    if (!filename) return '';
    const lastDot = filename.lastIndexOf('.');
    return lastDot === -1 ? filename : filename.slice(0, lastDot);
  }

  const isDataUrl = params.imageSource?.startsWith('data:');

  const thumbnailSource = params.imageSource
    ? isDataUrl
      ? params.imageSource
      : `${removeExtension(params.imageSource).replace(
          '/attachments/',
          '/thumbnails/'
        )}.png`
    : undefined;

  const actualImageUrl = params.imageSource || undefined;

  return (
    <div className={styles.root}>
      <div className={styles.row}>
        <RenderIf condition={!!thumbnailSource}>
          <img
            src={thumbnailSource}
            alt=""
            className={styles.thumbnail}
            style={{ cursor: actualImageUrl ? 'pointer' : undefined }}
            onClick={() => actualImageUrl && setShowImageModal(true)}
          />
        </RenderIf>
        {!params.disabled &&
          (params.imageSelectionAllowed !== false ? (
            <>
              <Button
                appearance="subtle"
                icon={<FolderOpenRegular />}
                aria-label="Select file"
                onClick={() => fileUploadRef.current?.click()}
              />
              <input
                type="file"
                name="file"
                accept={params.allowVideo ? 'image/*,video/*' : 'image/*'}
                ref={fileUploadRef}
                onChange={changeHandler}
                className={styles.hiddenInput}
              />
            </>
          ) : (
            <Button
              appearance="subtle"
              icon={<Camera24Regular />}
              aria-label={
                params.allowVideo ? 'Take photo or video' : 'Take photo'
              }
              onClick={() => setShowImageTaker(true)}
            />
          ))}
        <Input
          value={selectedFile?.name ?? params.value}
          readOnly
          disabled
          className={styles.fileName}
        />
        {!params.disabled &&
          (uploading ? (
            <Spinner />
          ) : (
            <Button
              appearance="subtle"
              icon={<CloudArrowUpRegular />}
              aria-label="Upload file"
              disabled={!isSelected}
              onClick={handleUploadClick}
            />
          ))}
        <RenderIf condition={!params.disabled && !!params.value}>
          <Button
            appearance="subtle"
            icon={<DeleteRegular />}
            aria-label="Remove file"
            onClick={() => {
              if (params.onUpload) {
                params?.onUpload(0, '', '');
              }
            }}
          />
        </RenderIf>
      </div>

      <ImagePreviewDialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        content={
          actualImageUrl
            ? { imageContent: actualImageUrl, location: params.imageLocation }
            : null
        }
      />
      <ImageTaker
        open={showImageTaker}
        allowVideo={params.allowVideo}
        onClose={() => setShowImageTaker(false)}
        onCapture={(dataUrl, location) => {
          setShowImageTaker(false);
          const arr = dataUrl.split(',');
          const mimeMatch = arr[0].match(/:(.*?);/);
          const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
          const bstr = atob(arr[1]);
          let n = bstr.length;
          const u8arr = new Uint8Array(n);
          while (n--) u8arr[n] = bstr.charCodeAt(n);
          const file = new File([u8arr], getCapturedMediaFileName(mime), {
            type: mime,
          });
          setSelectedFile(file);
          setIsSelected(true);
          setCapturedLocation(location);
        }}
      />
    </div>
  );
};
